import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useEffect, useMemo, useState, useCallback } from "react";
import { DashboardHeader, Footer, Header, SellerSidebar } from "@/components";
import { Toaster } from "sonner";
import { Outlet } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  useAttributes,
  useCombineProducts,
  useCustomPaginatedProducts,
  useFetchBrands,
  useFetchCategories,
} from "@/hooks";
import {
  setProductsLoading,
  setEnrichedProductList,
  setProductInfos,
  setAllVariantAttributes,
  setProductsForDisplay,
  setShouldFetchProducts,
} from "@/store/slices/sellerDashboardSlice";

import ls from "@/utils/SecureLS";
import { LoaderCircle } from "lucide-react";
import { ProductInfo } from "@/types/AppTypes";
import { initializeAuthFromLocalStorage } from "@/utils/helper/initializeuserInfoFromLS";
import { useSellerInfo } from "@/hooks/useSellerInfo";

const SellerDashboard = () => {
  const dispatch = useAppDispatch();
  const userLs = ls.get("user") || "";

  const user = JSON.parse(userLs);
  const userId = user?.user_id || "";
 

  const { brands: brandsRedux, categories: categoriesRedux } = useAppSelector((state) => state.app);
  const { fetchSellerInfo } = useSellerInfo();

  const {
    productsForDisplay: productsForDisplayRedux,
    allVariantAttributes,
    productsLoading,
    shouldFetchProducts,
    brands: sellerBrandsRedux,
    categories: sellerCategoriesRedux,
  } = useAppSelector((state) => state.seller);

  const [fetchFlags, setFetchFlags] = useState({
    brands: false,
    categories: false,
    attributes: false,
  });

  useEffect(() => {
    initializeAuthFromLocalStorage(dispatch);
    if (brandsRedux && brandsRedux.length > 0) {
      dispatch({ type: "seller/setBrands", payload: brandsRedux });
    }

    if (categoriesRedux && categoriesRedux.length > 0) {
      dispatch({ type: "seller/setCategories", payload: categoriesRedux });
    }

    setFetchFlags({
      brands:
        (!brandsRedux || brandsRedux.length === 0) &&
        (!sellerBrandsRedux || sellerBrandsRedux.length === 0),
      categories:
        (!categoriesRedux || categoriesRedux.length === 0) &&
        (!sellerCategoriesRedux || sellerCategoriesRedux.length === 0),
      attributes: !allVariantAttributes || allVariantAttributes.length === 0,
    });

    dispatch(
      setShouldFetchProducts(!productsForDisplayRedux || productsForDisplayRedux.length === 0)
    );

    fetchSellerInfo();

    // eslint-disable-next-line
  }, []);

  const { localBrands, loading: brandsLoading } = useFetchBrands({
    skip: !fetchFlags.brands,
  });
  const { localCategories, loading: categoriesLoading } = useFetchCategories({
    skip: !fetchFlags.categories,
  });

  const variablesP = useMemo(() => {
    return {
      filter: {
        is_deleted: { eq: false },
        is_active: { eq: true },
        seller_id: { eq: userId },
      },
    };
  }, [userId]);
  const {
    products: productInfos,
    loadPage,
    productsForDisplay,
  } = useCustomPaginatedProducts({
    skip: !shouldFetchProducts,
    dispatch,
    setEnrichedProductList,
    setProductsLoading,
    infoVariablesP: variablesP,
  });

  const brands = useMemo(
    () => (brandsRedux.length > 0 ? brandsRedux : localBrands),
    [brandsRedux, localBrands]
  );
  const categories = useMemo(
    () => (categoriesRedux.length > 0 ? categoriesRedux : localCategories),
    [categoriesRedux, localCategories]
  );

  const { loading } = useAttributes({
    skip: !fetchFlags.attributes,
    dispatch,
    setAllVariantAttributes,
  });
  const { combineProducts } = useCombineProducts({
    productsForDisplay,
    productInfos,
    brands,
    categories,
    dispatch,
    setProductsForDisplay,
  });
  const memoizedCombineProducts = useCallback(() => {
    combineProducts();
  }, [combineProducts]);

  useEffect(() => {
    if (shouldFetchProducts && productInfos?.length === 0) {
      loadPage();
    }
    // eslint-disable-next-line
  }, [shouldFetchProducts]);

  useEffect(() => {
    const allLoaded = !productsLoading && !brandsLoading && !categoriesLoading && !loading;

    if (allLoaded && shouldFetchProducts) {
      dispatch(
        completeProductsLoad({
          shouldFetchProducts: false,
          productInfos,
        })
      );

      memoizedCombineProducts();
      setFetchFlags((prev) => ({
        ...prev,
        brands: false,
        categories: false,
        attributes: false,
      }));
    }
  }, [productsLoading, brandsLoading, categoriesLoading, loading]);

  return (
   
   
    <SidebarProvider className="">
      <SellerSidebar variant="inset" className="" />
      <SidebarInset className=" flex flex-1 flex-col bg-white border border-gray-200">
        <DashboardHeader />
        <div className="flex flex-1 flex-col">
          <div className="@container/main flex flex-1 flex-col gap-2 min-h-full">
            <Toaster />
            {productsLoading || loading || categoriesLoading || brandsLoading ? (
              <div className="flex items-center justify-center flex-1 min-h-[400px]">
                <div className="flex flex-col items-center gap-4">
                  <div className="flex items-center">
                    <LoaderCircle className="w-6 h-6 animate-spin mr-4" />
                    <span>Fetching your inventory data. This won't take long!</span>
                  </div>
                  {/* Optional: Add a timeout message */}
                  <div className="text-sm text-gray-500 text-center max-w-md">
                    If this takes too long, try refreshing the page
                  </div>
                </div>
              </div>
          ) : (
              <>

              <Outlet />
              <Footer user={user}/>
              </>
            )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
    
  );
};

function completeProductsLoad({
  shouldFetchProducts,
  productInfos,
}: {
  shouldFetchProducts: boolean;
  productInfos: ProductInfo[];
}) {
  return (dispatch: any) => {
    dispatch(setShouldFetchProducts(shouldFetchProducts));
    dispatch(setProductInfos(productInfos));
  };
}

export default SellerDashboard;
